// /mnt/d/erp_integracao/frontend/src/pages/Configuracoes.jsx

import React, { useState, useEffect, useRef, useCallback } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';
import StatusBadge from '../components/StatusBadge';
import Modal from '../components/Modal';
import { MessageSquare, CheckCircle, XCircle, RefreshCw, Send, Smartphone, AlertCircle, Zap, RotateCw } from 'lucide-react';
import apiService from '@/services/apiService'; // ✅ IMPORTAR apiService

// ✅ URL APENAS PARA ABRIR A JANELA DO QR CODE
// Sempre same-origin: o backend (via proxy em /whatsapp) decide se repassa
// para o servidor local ou remoto, então a página não precisa mais saber
// disso (ver .clinerules/11-whatsapp-local-remoto.md)
const getWhatsAppPageURL = () => `${window.location.origin}/whatsapp`;

const WHATSAPP_PAGE_URL = getWhatsAppPageURL();

// URL pública que deve ser cadastrada no painel da Meta (webhook da API oficial)
const WEBHOOK_OFICIAL_URL = `${window.location.origin}/whatsapp-oficial/webhook`;

const NOMES_PROVEDOR = {
  webjs: 'WhatsApp Web (QR Code)',
  oficial: 'API Oficial (Meta)',
};

console.log('🔗 WhatsApp Page URL:', WHATSAPP_PAGE_URL);
console.log('🌍 Hostname:', window.location.hostname);

export default function Configuracoes() {
  const [loading, setLoading] = useState(true);
  const [testando, setTestando] = useState(false);
  const [conectando, setConectando] = useState(false);
  const [atualizandoQR, setAtualizandoQR] = useState(false);
  const [limpandoSessao, setLimpandoSessao] = useState(false);
  const [mostrarModalLimparSessao, setMostrarModalLimparSessao] = useState(false);
  const [whatsappStatus, setWhatsappStatus] = useState(null);
  const [telefoneTest, setTelefoneTest] = useState('');
  const [emailTest, setEmailTest] = useState('');
  const [testandoEmail, setTestandoEmail] = useState(false);
  const [modoWhatsapp, setModoWhatsapp] = useState('local');
  const [ipRemoto, setIpRemoto] = useState('');
  // Provedor salvo no backend (o que o sistema usa de fato) x aba visualizada
  const [provedorAtivo, setProvedorAtivo] = useState('webjs');
  const [abaWhatsapp, setAbaWhatsapp] = useState('webjs');
  const [configSalva, setConfigSalva] = useState({ modo: 'local', ip_remoto: null });
  const [statusOficial, setStatusOficial] = useState(null);
  const [carregandoStatusOficial, setCarregandoStatusOficial] = useState(false);
  const [salvandoConfigServidor, setSalvandoConfigServidor] = useState(false);
  const [provisorioAtivo, setProvisorioAtivo] = useState(false);
  const [provisorioTexto, setProvisorioTexto] = useState('');
  const [salvandoProvisorio, setSalvandoProvisorio] = useState(false);
  const intervalRef = useRef(null);
  const qrWindowRef = useRef(null);
  const alertShownRef = useRef(false);

  // ✅ CORRIGIDO: Usar apiService ao invés de fetch direto
  const verificarStatus = useCallback(async () => {
    try {
      // ✅ USA O PROXY DO VITE: /whatsapp/status -> localhost:21465/status
      const response = await apiService.whatsapp.getStatus();
      
      console.log('🔍 Status WhatsApp completo:', response.data);
      
      // ✅ LÓGICA SIMPLIFICADA: conectado = success && data?.connected
      const estaConectado = response.data.success && response.data.data?.connected === true;
      
      const novoStatus = {
        conectado: estaConectado,
        state: response.data.state || response.data.data?.state || 'DISCONNECTED',
        initializing: response.data.data?.initializing || false,
        hasQR: response.data.data?.hasQR || false,
        info: response.data.data?.data?.me ? {
          number: response.data.data.data.me.user,
          name: response.data.data.data.me.pushname || 'WhatsApp'
        } : null
      };

      console.log('✅ Status processado:', {
        conectado: estaConectado,
        state: novoStatus.state,
        hasInfo: !!novoStatus.info
      });
      
      setWhatsappStatus(novoStatus);

      // ✅ DETECTAR CONEXÃO ESTABELECIDA
      if (estaConectado && !alertShownRef.current) {
        console.log('🎉 WhatsApp CONECTADO detectado!');
        alertShownRef.current = true;
        
        setTimeout(() => {
          // Parar polling
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
            console.log('🛑 Polling interrompido');
          }

          // Fechar janela QR
          if (qrWindowRef.current && !qrWindowRef.current.closed) {
            qrWindowRef.current.close();
            qrWindowRef.current = null;
            console.log('🚪 Janela do QR Code fechada');
          }

          setConectando(false);
          alert('✅ WhatsApp conectado com sucesso!\n\nVocê já pode usar o sistema de cobrança.');
        }, 2000);

        return true;
      }
      
      return estaConectado;
    } catch (error) {
      console.error('❌ Erro ao verificar status:', error);
      setWhatsappStatus({
        conectado: false,
        state: 'ERROR',
        error: error.message
      });
      return false;
    }
  }, []);

  useEffect(() => {
    carregarDados();
    
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (qrWindowRef.current && !qrWindowRef.current.closed) {
        qrWindowRef.current.close();
        qrWindowRef.current = null;
      }
    };
  }, []);

  const carregarDados = async () => {
    try {
      setLoading(true);
      alertShownRef.current = false;
      await Promise.all([verificarStatus(), carregarConfigServidor(), carregarProvisorio(), carregarStatusOficial()]);
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
    } finally {
      setLoading(false);
    }
  };

  const carregarConfigServidor = async () => {
    try {
      const response = await apiService.configuracoes.getWhatsapp();
      const config = response.data?.data;
      if (config) {
        setModoWhatsapp(config.modo || 'local');
        setIpRemoto(config.ip_remoto || '');
        setConfigSalva({ modo: config.modo || 'local', ip_remoto: config.ip_remoto || null });
        setProvedorAtivo(config.provedor || 'webjs');
        setAbaWhatsapp(config.provedor || 'webjs');
      }
    } catch (error) {
      console.error('Erro ao carregar configuração do servidor WhatsApp:', error);
    }
  };

  const carregarStatusOficial = async () => {
    try {
      setCarregandoStatusOficial(true);
      const response = await apiService.configuracoes.getStatusWhatsappOficial();
      setStatusOficial(response.data);
    } catch (error) {
      console.error('Erro ao consultar WhatsApp API oficial:', error);
      setStatusOficial({ success: false, state: 'OFFLINE', error: error.message });
    } finally {
      setCarregandoStatusOficial(false);
    }
  };

  const carregarProvisorio = async () => {
    try {
      const response = await apiService.whatsappGrupo.listarAgendamentos();
      const item = (response.data?.data || []).find(a => a.chave === 'provisorio');
      if (item) {
        setProvisorioAtivo(!!Number(item.ativo));
        setProvisorioTexto(item.texto || '');
      }
    } catch (error) {
      console.error('Erro ao carregar configuração de número provisório:', error);
    }
  };

  const salvarProvisorio = async () => {
    try {
      setSalvandoProvisorio(true);
      await apiService.whatsappGrupo.atualizarAgendamento('provisorio', {
        texto: provisorioTexto,
        ativo: provisorioAtivo ? 1 : 0,
      });
      alert('✅ Configuração de número provisório salva com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar configuração de número provisório:', error);
      alert('❌ Erro ao salvar: ' + (error.response?.data?.error || error.message));
    } finally {
      setSalvandoProvisorio(false);
    }
  };

  // Salva o provedor da aba atual. Só um provedor fica ativo: salvar uma aba
  // desativa a outra (todo envio passa a ir para o provedor salvo).
  const salvarConfigServidor = async (provedor) => {
    if (provedor === 'webjs' && modoWhatsapp === 'remoto' && !ipRemoto.trim()) {
      alert('Digite o IP da VPS remota');
      return;
    }

    const trocandoProvedor = provedor !== provedorAtivo;
    if (trocandoProvedor && !confirm(
      `Ativar ${NOMES_PROVEDOR[provedor]}?\n\n` +
      `${NOMES_PROVEDOR[provedorAtivo]} será desativado: cobranças, PIX e testes passam a ser enviados pelo novo provedor.`
    )) {
      return;
    }

    // Na aba oficial, preserva a config local/remoto já salva do webjs
    const modo = provedor === 'webjs' ? modoWhatsapp : configSalva.modo;
    const ip = provedor === 'webjs' ? ipRemoto.trim() : (configSalva.ip_remoto || '');

    try {
      setSalvandoConfigServidor(true);
      await apiService.configuracoes.salvarWhatsapp(provedor, modo, ip);
      alert(trocandoProvedor
        ? `✅ ${NOMES_PROVEDOR[provedor]} ativado!`
        : '✅ Configuração do servidor WhatsApp salva com sucesso!');
      await carregarDados();
    } catch (error) {
      console.error('Erro ao salvar configuração do servidor WhatsApp:', error);
      alert('❌ Erro ao salvar: ' + (error.response?.data?.error || error.message));
    } finally {
      setSalvandoConfigServidor(false);
    }
  };

  // ✅ CORRIGIDO: Abrir QR Code - usa URL direta apenas para popup
  const iniciarConexao = () => {
    console.log('🚀 Abrindo página do QR Code...');
    
    alertShownRef.current = false;
    
    const qrWindow = window.open(
      `${WHATSAPP_PAGE_URL}/qr-custom`,
      'WhatsApp QR Code',
      'width=600,height=800,left=100,top=100'
    );
    
    if (!qrWindow) {
      alert('❌ Popup bloqueado! Permita popups para este site e tente novamente.');
      return;
    }
    
    qrWindowRef.current = qrWindow;
    setConectando(true);
    iniciarPollingStatus();
    
    console.log('✅ Janela QR Code aberta!');
  };

  // ✅ CORRIGIDO: abre a janela ANTES de qualquer await — chamada após um
  // await/setTimeout perde o vínculo com o clique do usuário e o navegador
  // bloqueia o popup silenciosamente (sem erro nenhum). A qr-custom.html já
  // faz polling/retry sozinha até o novo QR ficar disponível.
  const forcarAtualizacaoQR = async () => {
    if (qrWindowRef.current && !qrWindowRef.current.closed) {
      qrWindowRef.current.close();
    }

    const novaJanela = window.open(
      `${WHATSAPP_PAGE_URL}/qr-custom`,
      'WhatsApp QR Code',
      'width=600,height=800,left=100,top=100'
    );

    if (!novaJanela) {
      alert('❌ Popup bloqueado! Permita popups para este site e tente novamente.');
      return;
    }

    qrWindowRef.current = novaJanela;
    console.log('✅ Janela QR Code aberta');

    try {
      setAtualizandoQR(true);
      console.log('🔄 Forçando atualização do QR Code...');

      // ✅ USA O PROXY: /whatsapp/restart -> localhost:21465/restart
      await apiService.whatsapp.iniciar();

      console.log('✅ Cliente reiniciado - novo QR Code será gerado');

      if (!intervalRef.current) {
        iniciarPollingStatus();
      }

      setConectando(true);

    } catch (error) {
      console.error('❌ Erro ao atualizar QR:', error);
      alert('Erro ao atualizar QR Code: ' + error.message);
    } finally {
      setAtualizandoQR(false);
    }
  };

  const iniciarPollingStatus = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    console.log('🔄 Iniciando polling de status (a cada 2 segundos)...');

    verificarStatus();

    intervalRef.current = setInterval(async () => {
      await verificarStatus();
    }, 2000);

    setTimeout(() => {
      if (intervalRef.current) {
        console.log('⏱️ Timeout do polling (5 minutos)');
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        setConectando(false);
        
        verificarStatus().then(conectado => {
          if (!conectado && !alertShownRef.current) {
            alert('⏱️ Tempo limite excedido.\n\nTente conectar novamente.');
          }
        });
      }
    }, 5 * 60 * 1000);
  };

  // ✅ CORRIGIDO: Usar apiService
  const desconectar = async () => {
    if (!confirm(
      'Deseja realmente desconectar o WhatsApp?\n\n' +
      'O número atual será desvinculado (como remover em "Aparelhos conectados" no celular) ' +
      'e a sessão salva será apagada. Para reconectar, será preciso escanear o QR Code novamente.'
    )) {
      return;
    }

    try {
      await apiService.whatsapp.desconectar();
      alert('✅ WhatsApp desconectado com sucesso!');
      alertShownRef.current = false;
      setWhatsappStatus({
        conectado: false,
        state: 'DISCONNECTED'
      });
      await carregarDados();
    } catch (error) {
      console.error('Erro ao desconectar:', error);
      alert('❌ Erro ao desconectar: ' + error.message);
    }
  };

  const limparSessao = () => {
    setMostrarModalLimparSessao(true);
  };

  const confirmarLimparSessao = async () => {
    setMostrarModalLimparSessao(false);

    try {
      setLimpandoSessao(true);
      await apiService.whatsapp.limparSessao();
      alertShownRef.current = false;
      setWhatsappStatus({ conectado: false, state: 'DISCONNECTED' });
      alert('✅ Limpeza iniciada!\n\nO servidor está reiniciando. Aguarde alguns segundos e clique em "Atualizar" para ver o novo status.');
    } catch (error) {
      console.error('Erro ao limpar sessão:', error);
      alert('❌ Erro ao limpar sessão: ' + error.message);
    } finally {
      setLimpandoSessao(false);
    }
  };

  // ✅ CORRIGIDO: Usar apiService
  const testarEnvio = async () => {
    if (!telefoneTest) {
      alert('Digite um número de telefone');
      return;
    }

    try {
      setTestando(true);
      
      await apiService.whatsapp.enviarMensagem(
        telefoneTest,
        '🎉 *Teste de Conexão WhatsApp!*\n\nSe você recebeu esta mensagem, a integração está funcionando perfeitamente! ✅'
      );
      
      alert('✅ Mensagem de teste enviada com sucesso!');
    } catch (error) {
      console.error('Erro ao testar envio:', error);
      alert('❌ Erro ao enviar mensagem: ' + error.message);
    } finally {
      setTestando(false);
    }
  };

  // ✅ NOVO: Teste de envio de email
  const testarEmail = async () => {
    if (!emailTest) {
      alert('Digite um endereço de email');
      return;
    }

    // Validar formato do email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailTest)) {
      alert('❌ Email inválido! Digite um endereço de email válido.');
      return;
    }

    try {
      setTestandoEmail(true);
      console.log('🧪 Testando envio de email para:', emailTest);
      
      const resultado = await apiService.email.testar(emailTest);
      
      console.log('✅ Resultado do teste:', resultado);
      
      if (resultado.success) {
        alert(
          `✅ Email de teste enviado com sucesso!\n\n` +
          `Destinatário: ${emailTest}\n` +
          `Cópia: contato@elidaeletro.com.br\n\n` +
          `Verifique sua caixa de entrada (pode levar alguns minutos).`
        );
        setEmailTest(''); // Limpar campo após sucesso
      } else {
        throw new Error(resultado.message || 'Erro desconhecido');
      }
      
    } catch (error) {
      console.error('❌ Erro ao testar email:', error);
      alert(
        `❌ Erro ao enviar email de teste:\n\n` +
        `${error.message}\n\n` +
        `Verifique:\n` +
        `• Configurações de SMTP no .env\n` +
        `• Conexão com a internet\n` +
        `• Servidor de email (smtp.dreamhost.com)`
      );
    } finally {
      setTestandoEmail(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <LoadingSpinner size="lg" text="Carregando configurações..." />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Configurações</h1>
          <p className="text-gray-600 mt-1">Configure integrações do sistema</p>
        </div>
        <button
          onClick={carregarDados}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 disabled:opacity-50 transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Atualizar
        </button>
      </div>

      {/* Conexão WhatsApp: um provedor ativo por vez (whatsapp-web.js ou API oficial) */}
      <div className="bg-white rounded-lg shadow">
        <div className="flex items-center gap-3 px-6 pt-6 mb-4">
          <Smartphone className="w-6 h-6 text-green-600" />
          <h2 className="text-lg font-semibold text-gray-900">Conexão WhatsApp</h2>
        </div>

        <div className="flex border-b border-gray-200 px-6">
          {['webjs', 'oficial'].map((provedor) => (
            <button
              key={provedor}
              onClick={() => setAbaWhatsapp(provedor)}
              className={`-mb-px px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                abaWhatsapp === provedor
                  ? 'border-green-600 text-green-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {NOMES_PROVEDOR[provedor]}
              {provedorAtivo === provedor && (
                <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-800">Ativo</span>
              )}
            </button>
          ))}
        </div>

        <div className="p-6 space-y-6">
          {abaWhatsapp !== provedorAtivo && (
            <div className="flex items-start gap-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-900">
              <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <p>
                Este provedor está <strong>desativado</strong>. Ao salvar, ele passa a ser usado pelo sistema
                e <strong>{NOMES_PROVEDOR[provedorAtivo]}</strong> será desativado.
              </p>
            </div>
          )}

          {abaWhatsapp === 'webjs' && (
            <>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Onde o servidor WhatsApp está rodando
                  </label>
                  <select
                    value={modoWhatsapp}
                    onChange={(e) => setModoWhatsapp(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="local">VPS Local (padrão)</option>
                    <option value="remoto">VPS Remota (via rede Nebula)</option>
                  </select>
                </div>

                {modoWhatsapp === 'remoto' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      IP da VPS remota (rede Nebula)
                    </label>
                    <input
                      type="text"
                      value={ipRemoto}
                      onChange={(e) => setIpRemoto(e.target.value)}
                      placeholder="100.64.0.5"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent font-mono"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      A porta é sempre 21465, mesma do servidor local. Não é necessário informá-la.
                    </p>
                  </div>
                )}

                <button
                  onClick={() => salvarConfigServidor('webjs')}
                  disabled={salvandoConfigServidor}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 transition-colors text-sm"
                >
                  {salvandoConfigServidor ? 'Salvando...' : provedorAtivo === 'webjs' ? 'Salvar' : 'Salvar e ativar'}
                </button>
              </div>

              {/* Status, QR Code e sessão só fazem sentido com o webjs ativo:
                  /whatsapp/* aponta sempre para o provedor ativo */}
              {provedorAtivo === 'webjs' && (
                <>
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      {whatsappStatus?.conectado ? (
                        <CheckCircle className="w-8 h-8 text-green-600" />
                      ) : (
                        <XCircle className="w-8 h-8 text-red-600" />
                      )}
                      <div>
                        <p className="font-medium text-gray-900">
                          {whatsappStatus?.conectado ? '✅ Conectado' : '❌ Desconectado'}
                        </p>
                        <p className="text-sm text-gray-600 mt-1">
                          {whatsappStatus?.conectado 
                            ? 'WhatsApp pronto para enviar mensagens'
                            : whatsappStatus?.state === 'INITIALIZING' 
                              ? '🔄 Inicializando WhatsApp...'
                              : 'Conecte o WhatsApp para começar'
                          }
                        </p>
                        {whatsappStatus?.info && (
                          <div className="text-xs text-gray-500 mt-2 space-y-1">
                            <p>📱 Número: +{whatsappStatus.info.number}</p>
                            <p>👤 Nome: {whatsappStatus.info.name}</p>
                          </div>
                        )}
                        {whatsappStatus?.state && (
                          <div className="text-xs text-gray-400 mt-1">
                            Estado: {whatsappStatus.state}
                          </div>
                        )}
                      </div>
                    </div>
          
                    <div className="flex flex-col gap-2">
                      {whatsappStatus?.conectado ? (
                        <button
                          onClick={desconectar}
                          className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors text-sm"
                        >
                          Desconectar
                        </button>
                      ) : (
                        <button
                          onClick={iniciarConexao}
                          disabled={conectando}
                          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm flex items-center gap-2"
                        >
                          {conectando ? (
                            <>
                              <RefreshCw className="w-4 h-4 animate-spin" />
                              Conectando...
                            </>
                          ) : (
                            <>
                              <Smartphone className="w-4 h-4" />
                              Conectar WhatsApp
                            </>
                          )}
                        </button>
                      )}
                      <button
                        onClick={limparSessao}
                        disabled={limpandoSessao}
                        className="px-4 py-2 bg-gray-700 text-white rounded hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm flex items-center gap-2"
                        title="Apaga .wwebjs_auth, .wwebjs_cache e perfis do Chromium, depois reinicia o servidor"
                      >
                        {limpandoSessao ? (
                          <>
                            <RefreshCw className="w-4 h-4 animate-spin" />
                            Limpando...
                          </>
                        ) : (
                          <>
                            <Zap className="w-4 h-4" />
                            Apagar sessão antiga
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Botão para Forçar Novo QR Code */}
                  {conectando && !whatsappStatus?.conectado && (
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-start gap-3">
                          <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-medium text-orange-900">Problemas com o QR Code?</p>
                            <p className="text-sm text-orange-800 mt-1">
                              Se o QR Code não está aparecendo ou expirou, clique no botão ao lado para gerar um novo.
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={forcarAtualizacaoQR}
                          disabled={atualizandoQR}
                          className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700 disabled:opacity-50 transition-colors text-sm whitespace-nowrap ml-4"
                        >
                          {atualizandoQR ? (
                            <>
                              <RotateCw className="w-4 h-4 animate-spin" />
                              Atualizando...
                            </>
                          ) : (
                            <>
                              <RotateCw className="w-4 h-4" />
                              Gerar Novo QR Code
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Como Conectar */}
                  {!whatsappStatus?.conectado && !conectando && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                      <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
                        <Smartphone className="w-5 h-5" />
                        Pronto para Conectar?
                      </h3>
                      <p className="text-sm text-blue-800 mb-4">
                        Clique no botão <strong>"Conectar WhatsApp"</strong> acima. Uma janela será aberta com o QR Code para você escanear.
                      </p>
                      <div className="text-xs text-blue-700 space-y-1">
                        <p>• O QR Code atualiza automaticamente na janela</p>
                        <p>• A conexão é detectada automaticamente (em até 2 segundos)</p>
                        <p>• Esta página será atualizada quando você conectar</p>
                        <p>• A janela do QR Code fechará sozinha após conectar</p>
                      </div>
                      <p className="text-xs text-black mt-3 pt-3 border-t border-blue-200">
                        <strong>⚠️ "Apagar sessão antiga"</strong> só é necessário em caso de troca de número ou bloqueio/travamento pela META. No uso normal, basta conectar pelo QR Code.
                      </p>
                    </div>
                  )}

                  {/* Aguardando Conexão */}
                  {conectando && !whatsappStatus?.conectado && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                      <h3 className="font-semibold text-green-900 mb-3 flex items-center gap-2">
                        <RefreshCw className="w-5 h-5 animate-spin" />
                        Aguardando Conexão...
                      </h3>
                      <p className="text-sm text-green-800 mb-4">
                        Escaneie o QR Code na janela que foi aberta com seu celular.
                      </p>
                      <div className="text-xs text-green-700 space-y-2">
                        <p><strong>Como escanear:</strong></p>
                        <ol className="list-decimal list-inside space-y-1 ml-2">
                          <li>Abra o WhatsApp no seu celular</li>
                          <li>Vá em <strong>Configurações → Aparelhos conectados</strong></li>
                          <li>Toque em <strong>Conectar um aparelho</strong></li>
                          <li>Aponte para o QR Code na janela aberta</li>
                        </ol>
                        <p className="mt-3 pt-3 border-t border-green-300">
                          <strong>Status:</strong> Verificando conexão a cada 2 segundos...
                        </p>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {abaWhatsapp === 'oficial' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  {statusOficial?.connected ? (
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  ) : (
                    <XCircle className="w-8 h-8 text-red-600" />
                  )}
                  <div>
                    <p className="font-medium text-gray-900">
                      {statusOficial?.connected
                        ? '✅ Credenciais válidas'
                        : statusOficial?.state === 'NOT_CONFIGURED'
                          ? '⚙️ Não configurado'
                          : '❌ Indisponível'}
                    </p>
                    {!statusOficial?.connected && (statusOficial?.data?.error || statusOficial?.error) && (
                      <p className="text-sm text-red-700 mt-1">{statusOficial?.data?.error || statusOficial?.error}</p>
                    )}
                    {statusOficial?.data?.data?.me && (
                      <div className="text-xs text-gray-500 mt-2 space-y-1">
                        <p>📱 Número: +{statusOficial.data.data.me.user}</p>
                        <p>👤 Nome verificado: {statusOficial.data.data.me.pushname}</p>
                        {statusOficial.data.oficial?.qualidade && (
                          <p>📊 Qualidade: {statusOficial.data.oficial.qualidade}</p>
                        )}
                      </div>
                    )}
                    {statusOficial?.data?.oficial && (
                      <div className="text-xs text-gray-500 mt-2 space-y-1">
                        <p>
                          Webhook (mensagens recebidas):{' '}
                          {statusOficial.data.oficial.webhookAssinado && statusOficial.data.oficial.verifyTokenConfigurado
                            ? '✅ configurado'
                            : '⚠️ falta WA_APP_SECRET / WA_VERIFY_TOKEN'}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <button
                  onClick={carregarStatusOficial}
                  disabled={carregandoStatusOficial}
                  className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 disabled:opacity-50 transition-colors text-sm flex items-center gap-2"
                >
                  <RefreshCw className={`w-4 h-4 ${carregandoStatusOficial ? 'animate-spin' : ''}`} />
                  Revalidar
                </button>
              </div>

              <div className="text-sm text-gray-700 space-y-2">
                <p>
                  As credenciais ficam no <code className="bg-gray-100 px-1 rounded">.env</code> do servidor{' '}
                  <code className="bg-gray-100 px-1 rounded">backend/whatsapp-oficial</code> (token permanente,
                  Phone Number ID, App Secret e Verify Token) — não há QR Code.
                </p>
                <p>
                  URL do webhook para cadastrar no painel da Meta:
                  <br />
                  <code className="bg-gray-100 px-2 py-1 rounded text-xs break-all">{WEBHOOK_OFICIAL_URL}</code>
                </p>
              </div>

              <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-900">
                <AlertCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
                <p>
                  Regra da Meta: mensagem de texto livre só é entregue se o cliente mandou mensagem nas
                  últimas <strong>24h</strong>. Fora disso, só templates aprovados. Grupos (ofertas) continuam
                  pelo WhatsApp Web.
                </p>
              </div>

              <button
                onClick={() => salvarConfigServidor('oficial')}
                disabled={salvandoConfigServidor}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 transition-colors text-sm"
              >
                {salvandoConfigServidor ? 'Salvando...' : provedorAtivo === 'oficial' ? 'Salvar' : 'Salvar e ativar'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Whatsapp Provisório */}
      <div className="bg-white rounded-lg shadow p-6">
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={provisorioAtivo}
            onChange={e => setProvisorioAtivo(e.target.checked)}
            className="w-5 h-5 rounded border-gray-300 text-green-600 focus:ring-green-500"
          />
          <span className="text-lg font-semibold text-gray-900">Whatsapp provisório</span>
        </label>
        <p className="text-sm text-gray-600 mt-1 ml-8">
          Marque quando este número não é o principal da loja. Enquanto marcado, toda mensagem direta
          recebida (fora de grupo) é respondida automaticamente com o texto abaixo.
        </p>

        {provisorioAtivo && (
          <div className="mt-4 ml-8">
            <label className="text-sm font-medium text-gray-700 block mb-1.5">Resposta automática</label>
            <textarea
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              value={provisorioTexto}
              onChange={e => setProvisorioTexto(e.target.value)}
            />
          </div>
        )}

        <button
          onClick={salvarProvisorio}
          disabled={salvandoProvisorio}
          className="mt-4 ml-8 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
        >
          {salvandoProvisorio ? 'Salvando...' : 'Salvar'}
        </button>
      </div>

      {/* Teste de Envio WhatsApp */}
      {whatsappStatus?.conectado && (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-3 mb-4">
            <Send className="w-6 h-6 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">Teste WhatsApp</h2>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Número de Telefone (com DDD)
              </label>
              <input
                type="text"
                value={telefoneTest}
                onChange={(e) => setTelefoneTest(e.target.value)}
                placeholder="85999999999"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                Digite apenas números (ex: 85999999999). O código do país (+55) será adicionado automaticamente.
              </p>
            </div>

            <button
              onClick={testarEnvio}
              disabled={testando || !telefoneTest}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {testando ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Enviar Mensagem de Teste
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* ✅ NOVA SEÇÃO: Teste de Email */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center gap-3 mb-4">
          <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <h2 className="text-lg font-semibold text-gray-900">Teste de Email</h2>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email de Destino
            </label>
            <input
              type="email"
              value={emailTest}
              onChange={(e) => setEmailTest(e.target.value)}
              placeholder="seuemail@exemplo.com.br"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              Digite um email válido para receber uma mensagem de teste com link PIX simulado.
            </p>
          </div>

          <button
            onClick={testarEmail}
            disabled={testandoEmail || !emailTest}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {testandoEmail ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                Enviando Email...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Enviar Email de Teste
              </>
            )}
          </button>

          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <h4 className="font-medium text-purple-900 mb-2">📬 O que será enviado:</h4>
            <ul className="text-sm text-purple-800 space-y-1">
              <li>• Email com link PIX simulado</li>
              <li>• QR Code de exemplo em imagem</li>
              <li>• Código PIX "copia e cola"</li>
              <li>• Template completo do sistema</li>
              <li>• Cópia para contato@elidaeletro.com.br</li>
            </ul>
            <p className="text-xs text-purple-700 mt-3 pt-3 border-t border-purple-300">
              <strong>Servidor SMTP:</strong> smtp.dreamhost.com (porta 465)
            </p>
            <p className="text-xs text-purple-700 mt-1">
              <strong>Remetente:</strong> contato@elidaeletro.com.br
            </p>
          </div>
        </div>
      </div>

      {/* Informações Técnicas */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">ℹ️ Informações Técnicas</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">API Proxy</p>
            <p className="font-medium text-gray-900 font-mono text-sm">/whatsapp/*</p>
          </div>
          
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">QR Code Page</p>
            <p className="font-medium text-gray-900 font-mono text-sm">{WHATSAPP_PAGE_URL}</p>
          </div>

          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">Ambiente</p>
            <p className="font-medium text-gray-900 text-sm">
              {import.meta.env.DEV ? '🔧 Desenvolvimento' : '🚀 Produção'}
            </p>
          </div>

          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">Status do Servidor</p>
            <StatusBadge 
              status={whatsappStatus ? 'success' : 'danger'} 
              text={whatsappStatus ? 'Online' : 'Offline'} 
            />
          </div>

          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">Estado WhatsApp</p>
            <p className="font-medium text-gray-900">
              {whatsappStatus?.state || 'UNKNOWN'}
            </p>
          </div>

          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">Email SMTP</p>
            <p className="font-medium text-gray-900 text-sm">
              smtp.dreamhost.com:465
            </p>
          </div>
        </div>
      </div>

      {/* Troubleshooting */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
        <h3 className="font-semibold text-gray-900 mb-3">🔧 Solução de Problemas</h3>
        <div className="space-y-3 text-sm text-gray-700">
          <div>
            <p className="font-medium text-gray-900">✅ WhatsApp - "Proxy do Vite"</p>
            <p className="ml-4">→ Todas as chamadas usam /whatsapp/* (proxy)</p>
            <p className="ml-4">→ Apenas a janela do QR Code abre URL direta</p>
          </div>
          
          <div>
            <p className="font-medium text-gray-900">❌ WhatsApp - "Failed to fetch"</p>
            <p className="ml-4">→ Verifique se o servidor WhatsApp está rodando (porta 21465)</p>
            <p className="ml-4">→ Execute: <code className="bg-gray-200 px-2 py-1 rounded">pm2 list</code></p>
          </div>
          
          <div>
            <p className="font-medium text-gray-900">❌ WhatsApp - "QR Code não aparece"</p>
            <p className="ml-4">→ Verifique se permite popups neste site</p>
            <p className="ml-4">→ Aguarde 10 segundos e clique em "Gerar Novo QR Code"</p>
          </div>

          <div>
            <p className="font-medium text-gray-900">❌ Email - "Erro ao enviar"</p>
            <p className="ml-4">→ Verifique configurações do .env (IMAP_USER e IMAP_PASSWORD)</p>
            <p className="ml-4">→ Teste conexão: <code className="bg-gray-200 px-2 py-1 rounded">telnet smtp.dreamhost.com 465</code></p>
          </div>

          <div>
            <p className="font-medium text-gray-900">❌ Email - "Email não chegou"</p>
            <p className="ml-4">→ Verifique caixa de spam</p>
            <p className="ml-4">→ Aguarde até 5 minutos</p>
            <p className="ml-4">→ Verifique se o email de destino está correto</p>
          </div>
        </div>
      </div>

      <Modal
        isOpen={mostrarModalLimparSessao}
        onClose={() => setMostrarModalLimparSessao(false)}
        title="Apagar sessão antiga"
        size="sm"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-3 bg-orange-50 border border-orange-200 rounded">
            <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-orange-900">
              Isso vai <strong>desconectar o número atual</strong>
              {whatsappStatus?.info?.number ? (
                <> (<strong>+{whatsappStatus.info.number}</strong> — {whatsappStatus.info.name})</>
              ) : null}
              {' '}do WhatsApp. Você precisará escanear o QR Code novamente para conectar um número.
            </p>
          </div>

          <div className="text-sm text-gray-600">
            <p className="font-medium text-gray-900 mb-1">O que vai acontecer:</p>
            <ul className="list-disc list-inside space-y-0.5">
              <li>Parar o servidor WhatsApp</li>
              <li>Remover a sessão salva (.wwebjs_auth e .wwebjs_cache)</li>
              <li>Remover perfis residuais do Chromium</li>
              <li>Reiniciar o servidor automaticamente</li>
            </ul>
          </div>

          <p className="text-sm text-gray-700">Deseja prosseguir?</p>

          <div className="flex justify-end gap-2 pt-2">
            <button
              onClick={() => setMostrarModalLimparSessao(false)}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors text-sm"
            >
              Cancelar
            </button>
            <button
              onClick={confirmarLimparSessao}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors text-sm"
            >
              Apagar sessão e desconectar
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}